/**
 * A revision's diff, as a person reads it (§5.25): one line per thing that
 * changed, named by its path — `repos[api].startCmd`, `workflows[Feature].steps`
 * — with what it was and what it became.
 *
 * Everything in a diff was written by somebody's install or somebody's browser,
 * so it is rendered as data: its words in a monospace run, cut short, never
 * markup. A secret appears by its path alone; the diff never had a value to show.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import type React from 'react';
import { color, font } from '../../theme/tokens';
import { Badge } from '../../components/primitives';
import type { DiffEntry, RevisionDiff } from '../../lib/types';

/**
 * How much of one value a line holds. Three lines of it are in view (§46.6);
 * « Show all » opens the rest, and the full value is in the revision's document.
 */
const PREVIEW_CHARS = 2000;

/** Whether a value has nothing to say: drawn dim, never struck through (kit 74, F19). */
export function isUnset(value: unknown): boolean {
  return value === null || value === undefined;
}

/** A flat value in words: a person never reads `"…"` or `{"remote":…,"branch":null}`. A deep one stays JSON. */
function wordsOf(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (Array.isArray(value)) {
    const parts = value.map(wordsOf);
    return parts.every((part) => part !== null) ? parts.join(', ') : null;
  }
  if (typeof value !== 'object' || value === null) return null;
  const parts: string[] = [];
  for (const [key, inside] of Object.entries(value)) {
    if (inside === null || inside === undefined) {
      // A branch left empty is a branch in its own right: the repository's default.
      if (key === 'branch') parts.push('default branch');
      continue;
    }
    const word = wordsOf(inside);
    if (word === null || typeof inside === 'object') return null;
    parts.push(word);
  }
  return parts.join(' · ');
}

/** A value as a line shows it: its words, or JSON text for what has none, cut short. */
export function previewOf(value: unknown): string {
  if (value === undefined) return '';
  if (value === null) return 'not set';
  if (typeof value === 'object' && value !== null && 'truncated' in value && 'preview' in value) {
    return `${String((value as { preview: unknown }).preview).slice(0, PREVIEW_CHARS)}…`;
  }
  const text = wordsOf(value) ?? JSON.stringify(value) ?? String(value);
  return text.length > PREVIEW_CHARS ? `${text.slice(0, PREVIEW_CHARS)}…` : text;
}

/**
 * A change card in its compact line (redesign §46.6): the kind as a neutral
 * label — the colour never carries the kind (rule 15) — the key in mono, then
 * « Before » struck through in red and « After » in green.
 */
export function DiffView({ diff, empty = 'Nothing changed.' }: { diff: RevisionDiff; empty?: string }): React.JSX.Element {
  if (diff.length === 0) return <p style={{ fontSize: 13.5, color: color.inkFaint, margin: 0 }}>{empty}</p>;
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {diff.map((entry, index) => (
        <Change key={`${entry.path}-${index}`} entry={entry} />
      ))}
    </div>
  );
}

function Change({ entry }: { entry: DiffEntry }): React.JSX.Element {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '84px minmax(0,1fr)',
        gap: 12,
        alignItems: 'start',
        padding: '10px 12px',
        border: `1px solid ${color.line}`,
        borderRadius: 10,
        background: color.surface
      }}
    >
      <Badge tone="off">{entry.kind.toUpperCase()}</Badge>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: font.mono, fontSize: 12.5, color: color.ink, wordBreak: 'break-all', paddingTop: 3 }}>
          {entry.path}
        </div>
        {entry.kind !== 'added' && entry.before !== undefined && (
          <DiffValue
            label="Before"
            value={previewOf(entry.before)}
            tone={isUnset(entry.before) ? color.inkFaint : color.badText}
            struck={!isUnset(entry.before)}
          />
        )}
        {entry.kind !== 'removed' && entry.after !== undefined && (
          <DiffValue label="After" value={previewOf(entry.after)} tone={color.okText} />
        )}
      </div>
    </div>
  );
}

/** One side of a change: three lines in view, « Show all » for the rest. */
export function DiffValue({
  label,
  value,
  tone,
  struck = false
}: {
  label: string;
  value: string;
  tone: string;
  struck?: boolean;
}): React.JSX.Element {
  const ref = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  // Whether the value runs past its three lines, measured once it is drawn:
  // a short value has no « Show all » to offer.
  const [long, setLong] = useState(false);
  useLayoutEffect(() => {
    const node = ref.current;
    if (node && !open) setLong(node.scrollHeight > node.clientHeight + 1);
  }, [value, open]);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '48px minmax(0,1fr)', gap: 8, marginTop: 6 }}>
      <span style={{ fontFamily: font.mono, fontSize: 11, color: color.inkFaint, paddingTop: 1 }}>{label}</span>
      <div style={{ minWidth: 0 }}>
        <code
          ref={ref}
          style={{
            display: open ? 'block' : '-webkit-box',
            WebkitLineClamp: open ? undefined : 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontFamily: font.mono,
            fontSize: 12,
            lineHeight: 1.5,
            color: tone,
            textDecoration: struck ? 'line-through' : undefined,
            wordBreak: 'break-all',
            whiteSpace: 'pre-wrap'
          }}
        >
          {value}
        </code>
        {(long || open) && (
          <button
            type="button"
            onClick={() => setOpen((was) => !was)}
            aria-expanded={open}
            style={{
              background: 'none',
              border: 0,
              padding: 0,
              marginTop: 2,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 12.5,
              color: color.accentText
            }}
          >
            {open ? 'Show less' : 'Show all'}
          </button>
        )}
      </div>
    </div>
  );
}
